import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Buildings, Users, UserSwitch, WarningCircle, MagnifyingGlass,
  ShieldCheck, FileText, Check, X, Eye, PencilSimple, MapPin, Plus,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { GlassCard } from "@/components/glass/GlassCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { PageShell } from "@/components/layout/AppLayout";
import NewTenantWizard from "@/components/NewTenantWizard";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "@/components/ConfirmDialog";

const RoleChip = ({ role, is_global }) => {
  const color =
    role === "global_admin" ? "chip-critical" :
    role === "clariva_support" ? "chip-high" :
    role === "admin" ? "chip-medium" :
    role === "contract_manager" ? "chip-warning" :
    role === "manager" ? "chip-low" :
    "chip-neutral";
  return (
    <span className={`chip ${color}`}>{role}{is_global ? " · global" : ""}</span>
  );
};

export default function GlobalAdmin() {
  const { user, refreshMe, setToken } = useAuth();
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [impersonationLog, setImpersonationLog] = useState([]);
  const [q, setQ] = useState("");
  const [tenantFilter, setTenantFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [ticketStatusFilter, setTicketStatusFilter] = useState("open");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [pendingImpersonate, setPendingImpersonate] = useState(null); // { id, email }
  const [pendingStatus, setPendingStatus] = useState(null); // { id, email, status }
  const [editTenant, setEditTenant] = useState(null); // full tenant obj being edited
  const [editIndustries, setEditIndustries] = useState([]);
  const [editRegionsList, setEditRegionsList] = useState([]);
  const [savingTenant, setSavingTenant] = useState(false);
  const [tab, setTab] = useState("orgs");
  const [newTenantOpen, setNewTenantOpen] = useState(false);

  const isStaff = user?.is_global || ["global_admin", "clariva_support"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [t, u, tk, il] = await Promise.all([
        api.get("/global/tenants"),
        api.get("/global/users", { params: { q: q || undefined, tenant_id: tenantFilter !== "all" ? tenantFilter : undefined } }),
        api.get("/global/support/tickets", { params: { status: ticketStatusFilter !== "all" ? ticketStatusFilter : undefined } }),
        api.get("/global/impersonation-log", { params: { limit: 30 } }),
      ]);
      setTenants(t.data.tenants || []);
      setGroups(u.data.groups || []);
      setTickets(tk.data.tickets || []);
      setImpersonationLog(il.data.entries || []);
    } catch (e) {
      toast.error("Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [tenantFilter, ticketStatusFilter]);
  useEffect(() => {
    if (!isStaff) return;
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Load industry/region catalog when we first need it
  useEffect(() => {
    if (!isStaff || editIndustries.length) return;
    api.get("/compliance/frameworks").then(({ data }) => {
      setEditIndustries(data.industries || []);
      setEditRegionsList(data.regions || []);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [isStaff]);

  const openEditTenant = (t) => {
    setEditTenant({
      id: t.id,
      name: t.name,
      industry: t.industry || "general",
      regions: t.compliance_config?.regions || ["global"],
    });
  };

  const saveTenantProfile = async () => {
    if (!editTenant) return;
    if (!editTenant.regions?.length) { toast.error("Pick at least one region"); return; }
    setSavingTenant(true);
    try {
      await api.patch(`/global/tenants/${editTenant.id}`, {
        industry: editTenant.industry,
        regions: editTenant.regions,
      });
      toast.success(`${editTenant.name} updated`);
      setEditTenant(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingTenant(false); }
  };

  const impersonate = async (uid) => {
    const found = groups.flatMap(g => g.users || []).find(u => u.id === uid);
    setPendingImpersonate({ id: uid, email: found?.email || "this user", full_name: found?.full_name });
  };

  const confirmImpersonate = async () => {
    if (!pendingImpersonate) return;
    const uid = pendingImpersonate.id;
    setPendingImpersonate(null);
    try {
      const { data } = await api.post(`/global/impersonate/${uid}`);
      setToken(data.access_token);
      await refreshMe();
      toast.success(`You are now viewing as ${data.user.email}`);
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
    }
  };

  const setStatus = async (uid, status) => {
    const found = groups.flatMap(g => g.users || []).find(u => u.id === uid);
    setPendingStatus({ id: uid, email: found?.email || "this user", status });
  };

  const confirmStatus = async () => {
    if (!pendingStatus) return;
    const { id, status } = pendingStatus;
    setPendingStatus(null);
    try {
      await api.post(`/global/users/${id}/status`, { status });
      toast.success(`User ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const patchTicket = async (tid, patch) => {
    try {
      await api.patch(`/global/support/tickets/${tid}`, patch);
      toast.success("Ticket updated");
      if (patch.reply) setReplyText("");
      const fresh = await api.get("/global/support/tickets", { params: { status: ticketStatusFilter !== "all" ? ticketStatusFilter : undefined } });
      setTickets(fresh.data.tickets || []);
      if (selectedTicket) {
        const updated = (fresh.data.tickets || []).find((t) => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    } catch (e) { toast.error("Update failed"); }
  };

  if (!isStaff) {
    return (
      <PageShell title="Global administration" subtitle="Restricted area">
        <GlassCard className="p-8 text-center">
          <WarningCircle size={32} className="mx-auto text-amber-500 mb-2" />
          <div className="text-sm text-[var(--text-2)]">This area is restricted to Clariva staff.</div>
        </GlassCard>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Clariva HQ — Global Administration"
      subtitle={`Cross-tenant operations · ${tenants.length} organisations · ${groups.reduce((s, g) => s + g.users.length, 0)} users`}
    >
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="global-admin-tabs">
          <TabsTrigger value="orgs" data-testid="tab-orgs"><Buildings size={12} className="mr-1.5" /> Organisations</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users size={12} className="mr-1.5" /> Users by org</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets"><WarningCircle size={12} className="mr-1.5" /> Support tickets</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ShieldCheck size={12} className="mr-1.5" /> Impersonation log</TabsTrigger>
        </TabsList>

        {/* ORGS TAB */}
        <TabsContent value="orgs">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-xs text-[var(--text-3)]">{tenants.length} organisation{tenants.length === 1 ? "" : "s"}</div>
            <Button size="sm" onClick={() => setNewTenantOpen(true)} data-testid="new-tenant-btn" className="gap-2">
              <Plus size={13} /> New tenant
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenants.map((t) => (
              <GlassCard key={t.id} hover className="p-5" data-testid={`org-card-${t.slug}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <div className="text-xs text-[var(--text-3)] font-mono">{t.slug}</div>
                    <div className="text-base font-semibold tracking-tight mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{t.name}</div>
                  </div>
                  {t.slug === "clariva-hq" && <span className="chip chip-critical text-[10px]">HQ</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  <span className="chip chip-neutral gap-1 inline-flex items-center" data-testid={`org-industry-${t.slug}`}>
                    <Buildings size={10} weight="duotone" /> {t.industry || "general"}
                  </span>
                  {(t.compliance_config?.regions || []).slice(0, 3).map((r) => (
                    <span key={r} className="chip chip-neutral gap-1 inline-flex items-center">
                      <MapPin size={10} weight="duotone" /> {r}
                    </span>
                  ))}
                  {(t.compliance_config?.regions || []).length > 3 && (
                    <span className="text-[10px] text-[var(--text-3)]">+{t.compliance_config.regions.length - 3}</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div><div className="overline">Users</div><div className="text-lg font-mono">{t.user_count}</div></div>
                  <div><div className="overline">Contracts</div><div className="text-lg font-mono">{t.contract_count}</div></div>
                  <div><div className="overline">Active</div><div className="text-lg font-mono">{t.active_contracts}</div></div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => { setTenantFilter(t.id); setTab("users"); }} className="gap-2">
                    <Users size={12} /> Users
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEditTenant(t)} data-testid={`org-edit-${t.slug}`} className="gap-2">
                    <PencilSimple size={12} /> Edit
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>
        </TabsContent>

        {/* USERS TAB */}
        <TabsContent value="users" className="space-y-4">
          <GlassCard className="p-3 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name…" data-testid="global-user-search" className="ps-8 glass-subtle border-[var(--border)]" />
            </div>
            <Select value={tenantFilter} onValueChange={setTenantFilter}>
              <SelectTrigger data-testid="global-tenant-filter" className="w-full md:w-56 glass-subtle border-[var(--border)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organisations</SelectItem>
                {tenants.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </GlassCard>

          {loading ? (
            <div className="text-center py-10 text-sm text-[var(--text-3)]">Loading…</div>
          ) : groups.map((g) => (
            <GlassCard key={g.tenant.id || "unknown"} className="p-4" data-testid={`users-group-${g.tenant.slug}`}>
              <div className="flex items-center gap-2 mb-3">
                <Buildings size={14} weight="duotone" className="text-[var(--accent)]" />
                <div className="text-sm font-semibold">{g.tenant.name}</div>
                <span className="chip chip-neutral text-[10px]">{g.users.length} users</span>
              </div>
              <div className="divide-y divide-[var(--border)]">
                {g.users.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{u.full_name} <span className="text-[var(--text-3)] font-normal ml-1">· {u.email}</span></div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <RoleChip role={u.role} is_global={u.is_global} />
                        {u.status === "suspended" && <span className="chip chip-critical text-[10px]">suspended</span>}
                      </div>
                    </div>
                    {!u.is_global && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => impersonate(u.id)} data-testid={`impersonate-${u.id}`} className="gap-1.5 text-xs">
                          <UserSwitch size={12} /> Login as
                        </Button>
                        {user?.role === "global_admin" && (
                          u.status === "suspended" ? (
                            <Button size="sm" variant="ghost" onClick={() => setStatus(u.id, "active")} data-testid={`reactivate-${u.id}`} className="gap-1.5 text-xs">
                              <Check size={12} /> Activate
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => setStatus(u.id, "suspended")} data-testid={`suspend-${u.id}`} className="gap-1.5 text-xs text-[var(--danger)]">
                              <X size={12} /> Suspend
                            </Button>
                          )
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </GlassCard>
          ))}
        </TabsContent>

        {/* TICKETS TAB */}
        <TabsContent value="tickets" className="space-y-4">
          <div className="flex items-center gap-2">
            {["open", "in_progress", "resolved", "all"].map((s) => (
              <Button key={s} size="sm" variant={ticketStatusFilter === s ? "default" : "outline"} onClick={() => setTicketStatusFilter(s)} data-testid={`ticket-filter-${s}`} className="capitalize">{s.replace("_", " ")}</Button>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              {tickets.map((t) => (
                <GlassCard key={t.id} hover as="button" data-testid={`ticket-${t.id}`} onClick={() => setSelectedTicket(t)} className={`p-4 text-left w-full ${selectedTicket?.id === t.id ? "ring-2 ring-[var(--accent)]" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-[var(--text-3)] font-mono">{t.id.slice(0, 8)} · {t.reporter_email}</div>
                      <div className="text-sm font-semibold mt-0.5">{t.subject}</div>
                      <div className="text-xs text-[var(--text-2)] mt-1 line-clamp-2">{t.description}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className={`chip chip-${t.severity === "critical" ? "critical" : t.severity === "high" ? "high" : "neutral"} text-[10px]`}>{t.severity}</span>
                      <span className="chip chip-neutral text-[10px] capitalize">{t.status.replace("_", " ")}</span>
                    </div>
                  </div>
                </GlassCard>
              ))}
              {!tickets.length && <div className="text-sm text-[var(--text-3)] text-center py-8">No tickets matching this filter.</div>}
            </div>
            <GlassCard className="p-5">
              {selectedTicket ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex-1">
                      <div className="overline">Ticket · {selectedTicket.id.slice(0, 8)}</div>
                      <div className="text-base font-semibold mt-0.5">{selectedTicket.subject}</div>
                    </div>
                    <Select value={selectedTicket.status} onValueChange={(v) => patchTicket(selectedTicket.id, { status: v })}>
                      <SelectTrigger data-testid="ticket-status-select" className="w-40 glass-subtle border-[var(--border)] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="triaged">Triaged</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-xs text-[var(--text-3)] mb-2">{selectedTicket.reporter_name} · {selectedTicket.reporter_email} · {new Date(selectedTicket.created_at).toLocaleString()}</div>
                  <p className="text-sm leading-relaxed mb-4">{selectedTicket.description}</p>
                  {selectedTicket.page_url && (<div className="text-[11px] text-[var(--text-3)] mb-3">Reported from: <code>{selectedTicket.page_url}</code></div>)}
                  <div className="border-t border-[var(--border)] pt-3 space-y-2 max-h-64 overflow-auto">
                    {(selectedTicket.replies || []).map((r, i) => (
                      <div key={i} className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)] text-xs">
                        <div className="text-[10px] text-[var(--text-3)] mb-1">{r.author_name} · {new Date(r.at).toLocaleString()}</div>
                        <div>{r.body}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={3} placeholder="Write a reply…" data-testid="ticket-reply-input" className="glass-subtle border-[var(--border)] text-xs" />
                    <Button onClick={() => patchTicket(selectedTicket.id, { reply: replyText })} disabled={!replyText.trim()} data-testid="ticket-reply-send" className="mt-2 gap-2">Send reply</Button>
                  </div>
                </>
              ) : (
                <div className="text-sm text-[var(--text-3)] text-center py-8">Select a ticket.</div>
              )}
            </GlassCard>
          </div>
        </TabsContent>

        {/* IMPERSONATION AUDIT */}
        <TabsContent value="audit">
          <GlassCard className="p-4">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-widest text-[var(--text-3)]">
                <tr><th className="text-left py-2">Staff</th><th className="text-left">Target</th><th className="text-left">Started</th><th className="text-left">Ended</th></tr>
              </thead>
              <tbody>
                {impersonationLog.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--border)]">
                    <td className="py-2 text-xs">{r.actor_email}</td>
                    <td className="text-xs">{r.target_email}</td>
                    <td className="text-xs font-mono text-[var(--text-3)]">{new Date(r.started_at).toLocaleString()}</td>
                    <td className="text-xs font-mono text-[var(--text-3)]">{r.ended_at ? new Date(r.ended_at).toLocaleString() : <span className="chip chip-warning text-[10px]">active</span>}</td>
                  </tr>
                ))}
                {!impersonationLog.length && <tr><td colSpan={4} className="py-6 text-center text-[var(--text-3)] text-xs">No impersonations yet.</td></tr>}
              </tbody>
            </table>
          </GlassCard>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={!!pendingImpersonate}
        onOpenChange={(v) => { if (!v) setPendingImpersonate(null); }}
        title={`Log in as ${pendingImpersonate?.full_name || pendingImpersonate?.email}?`}
        description={`You will see Clariva exactly as ${pendingImpersonate?.email || "this user"} does — their contracts, workflows, and data. Every action you take will be attributed to you in the impersonation audit log. End the session anytime via the amber banner at the top of the screen.`}
        actionLabel="Start support session"
        onConfirm={confirmImpersonate}
        testId="impersonate-confirm-dialog"
      />

      <ConfirmDialog
        open={!!pendingStatus}
        onOpenChange={(v) => { if (!v) setPendingStatus(null); }}
        title={pendingStatus?.status === "suspended"
          ? `Suspend ${pendingStatus?.email}?`
          : `Reactivate ${pendingStatus?.email}?`}
        description={pendingStatus?.status === "suspended"
          ? "This user will immediately lose access to Clariva. Their data and audit trail are preserved. You can reactivate them anytime."
          : "This user will regain full access based on their existing role."}
        actionLabel={pendingStatus?.status === "suspended" ? "Suspend user" : "Reactivate"}
        destructive={pendingStatus?.status === "suspended"}
        onConfirm={confirmStatus}
        testId="user-status-confirm-dialog"
      />

      <Dialog open={!!editTenant} onOpenChange={(v) => !v && setEditTenant(null)}>
        <DialogContent className="max-w-2xl" data-testid="edit-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Edit organisation · {editTenant?.name}</DialogTitle>
            <DialogDescription>
              Industry drives default boilerplate, template, and compliance recommendations. Regions define which data-protection frameworks apply.
            </DialogDescription>
          </DialogHeader>

          {editTenant && (
            <div className="space-y-5">
              <div>
                <Label className="text-xs text-[var(--text-2)]">Industry</Label>
                <Select value={editTenant.industry} onValueChange={(v) => setEditTenant({ ...editTenant, industry: v })}>
                  <SelectTrigger data-testid="edit-tenant-industry-select" className="mt-1.5 glass-subtle border-[var(--border)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {editIndustries.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-[var(--text-2)]">Operating regions · pick one or more</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {editRegionsList.map((r) => {
                    const on = editTenant.regions.includes(r.id);
                    return (
                      <label
                        key={r.id}
                        data-testid={`edit-tenant-region-${r.id}`}
                        className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition ${on ? "bg-[var(--surface-strong)] border-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)] hover:border-[var(--border-strong)]"}`}
                      >
                        <Checkbox
                          checked={on}
                          onCheckedChange={(checked) => {
                            const next = checked
                              ? [...editTenant.regions, r.id]
                              : editTenant.regions.filter((x) => x !== r.id);
                            setEditTenant({ ...editTenant, regions: next });
                          }}
                          className="mt-0.5"
                        />
                        <div className="text-xs flex-1">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-[10px] text-[var(--text-3)] font-mono mt-0.5">{(r.compliance || []).join(", ").toUpperCase()}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setEditTenant(null)} data-testid="edit-tenant-cancel-btn">Cancel</Button>
            <Button onClick={saveTenantProfile} disabled={savingTenant} data-testid="edit-tenant-save-btn" className="gap-2">
              <Check size={14} /> {savingTenant ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NewTenantWizard open={newTenantOpen} onOpenChange={setNewTenantOpen} onCreated={() => load()} />
    </PageShell>
  );
}
